import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>
   Nowadays, nearly all of the available devices are digital and consequently work only in the Discrete-Time (DT) framework as illustrated in <a href=# data-img-alt="Figure 1. Discrete LTI System." data-img-src="${config.ASSETS_URL}/03/05/theory/figure-1.svg">Figure 1</a>.
   <figure class=figure>
   <img alt="Figure 1. Discrete LTI System." src="${config.ASSETS_URL}/03/05/theory/figure-1.svg">
   <figcaption class=caption>Figure 1. Discrete LTI System.</figcaption>
   </figure>
<p>
   The first step of the process, which is the conversion of the continuous signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   to a discrete signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   is called sampling. It is performed by an Analog to Digital Converter (ADC) so that:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close>]</mo>
            <mo class=MathClass-rel>≡</mo>
            <mi>x</mi>
            <mo class=MathClass-open>(</mo>
            <mi>n</mi>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   is the sampling period. It is the inverse of the sampling frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   . The sampling times are taken on a regular grid with the same time-step between two successive instants. In addition, the value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   is not exactly
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>
   due to quantization which consists in associating to a given value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   a quantified value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>q</mi>
         </mrow>
      </msub>
   </math>
   . Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   be the maximum peak value allowed by the device without any distorsion. Output values of the ADC are on a regular grid whose limits are
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   with a quantization step equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
                  <mi>i</mi>
                  <mi>t</mi>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>
   . Influence of quantification is not of major issue and won’t be detailed in this lesson. The sampled signal is then the input of a Discrete LTI system
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold-italic>
         <mi>S</mi>
      </mstyle>
   </math>
   . Its response is
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-open>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close>]</mo>
            <mo class=MathClass-rel>=</mo>
            <mstyle mathvariant=bold-italic>
               <mi>S</mi>
            </mstyle>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>(</mo>
               </mrow>
            </mstyle>
            <mi>x</mi>
            <mo class=MathClass-open>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close>]</mo>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>)</mo>
               </mrow>
            </mstyle>
         </math>
      <td class=eq-no>(2)
</table>
<p>This lesson presents how to compute this discrete response.<br class=newline>
<h3 class=sectionHead>Sampling and aliasing</h3>
<p>
   This section presents a consequence of sampling: aliasing. Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   be a CT signal whose Fourier transform is denoted by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <mo class=MathClass-open>(</mo>
      <mi>F</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . We will focus on this lesson on low-pass signals for which the support of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
   </math>
   is in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-close>]</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>;</mo>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>[</mo>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>∈</mo>
      <msup>
         <mrow>
            <mi>ℝ</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
   </math>
   . The sampling
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   can be formally written by a multiplication by the Dirac comb filter (which is an infinite sum of delayed Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   )
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>III</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi>∞</mi>
               </mrow>
            </munderover>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>n</mi>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>⇒</mo>
            <mi>d</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>x</mi>
            <mi>III</mi>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   The FT
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
      <mo class=MathClass-open>(</mo>
      <mi>F</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of the sampled signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   is then the convolution of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <mo class=MathClass-open>(</mo>
      <mi>F</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   by the FT of the Dirac comb which is a sum of delayed Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   . It leads to:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>D</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi>∞</mi>
               </mrow>
            </munderover>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>n</mi>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>The first applet of the principle section can help you to understand the influence of the sampling frequency on the spectrum of the sampled signal.
<p>
   The spectrum of the sampled signal is then the periodisation of the one
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
   </math>
   at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   composed of the original spectrum and an infinite number of replicas. If the sample frequency is sufficiently high, two successive replicas do not overlap and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <mo class=MathClass-open>(</mo>
      <mi>F</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be reobtained by a low-pass filter.<br class=newline>
<p>
   To avoid overlaps, it is necessary to determine the constraint between
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   . To do so, and due to periodicity, we just need that the upper bound of the original spectrum is lower than the lower bound of the first replica (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   ). Hence
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>a</mi>
                  <mi>x</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≤</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>a</mi>
                  <mi>x</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>⇒</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≥</mo>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>a</mi>
                  <mi>x</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   The sampling frequency should then be at least equal to two times
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   . This constraint is known as the Shannon criteria also called Nyquist theorem. The limit frequency is called Shannon frequency, Nyquist frequency or folding frequency. In a practical framework, in order to limit this overlapping, the analog signal is systematically low-pass filtered (by an anti-aliasing filter) so as to debase the information contained in the raw signal by cancelling the information contained in the frequency bands that have no relevance.<br class=newline>
<p>Overlap is also called <strong><span class=ecbx-1000>aliasing</span></strong>. You can see and hear in the second applet of the principle part what is the consequence of a two low sampling frequency for a pure tone.<br class=newline>
<h3 class=sectionHead>Fourier transform of a discrete signal</h3>
<p>The Discrete Fourier Transform (DFT) is defined in two steps. First the continuous integral is approximated by a simple rectangle quadrature:
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>≈</mo>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi>∞</mi>
               </mrow>
            </munderover>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>n</mi>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi>j</mi>
                  <mi>π</mi>
                  <mi>F</mi>
                  <mi>n</mi>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   Then the sum is truncated to the first
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   positive integers. This corresponds to the windowing of the signal by a rectangular window
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>≈</mo>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </munderover>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>n</mi>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi>j</mi>
                  <mi>π</mi>
                  <mi>F</mi>
                  <mi>n</mi>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   This truncation will generate ripples (the FT of a rectangular window is a sinus cardinal). These one can be avoided by the use of dedicated windows (Hann, Hamming, Blackman) but this is out of the scope of the present lesson. The Fourier Transform is then only calculated at discrete frequencies
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>k</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>N</mi>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mo class=MathClass-punc>.</mo>
      <mi>N</mi>
   </math>
   . This leads to the definition of the DFT:
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>k</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <mo class=MathClass-rel>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </munderover>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi>j</mi>
                  <mi>π</mi>
                  <mi>k</mi>
                  <mi>n</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>N</mi>
               </mrow>
            </msup>
            <mi>k</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mi>N</mi>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <mo class=MathClass-open>[</mo>
      <mi>k</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   corresponds to the component at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>k</mi>
         </mrow>
      </msub>
   </math>
   and note that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   has disappeared so as standardise the definition. The proof of the periodicity of the DFT is straightforward so that the frequency range of interest is an interval of length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   . In the definition, this interval is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>[</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>;</mo>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>[</mo>
   </math>
   but most of the times, the chosen interval is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-close>]</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>;</mo>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-open>[</mo>
   </math>
   .<br class=newline>
<p>The Inverse Discrete Fourier Transform (IDFT) is defined as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </mfrac>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>k</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </munderover>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>k</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>j</mi>
                  <mi>π</mi>
                  <mi>k</mi>
                  <mi>n</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>N</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   Compared to the continuous FT, note the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi>N</mi>
   </math>
   scaling factor so as to ensure that the two discrete transfors are mutual inverses.<br class=newline>
<p>
   In definition (8), the number of discrete frequencies is the same than the number of points of the time signal. A method to increase the frequency precision, with the same signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   is to add
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   zeros at the end of the signal. This leads to
<table class=equation>
   <tr>
      <td style="font-size: 4px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>k</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <mo class=MathClass-rel>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>P</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </munderover>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi>j</mi>
                  <mi>π</mi>
                  <mi>k</mi>
                  <mi>n</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi>N</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>P</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </munderover>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi>j</mi>
                  <mi>π</mi>
                  <mi>k</mi>
                  <mi>n</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi>N</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>P</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mi>k</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mi>N</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>P</mi>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   The discrete frequencies are now
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>k</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>P</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . The DFT curve is then smoother. In addition, in the case of a pure sinusoid, it allows a better precision on its frequency. Nevertheless, this mathematical trick does not increase the frequency resolution (ability to distinguish frequencies whose difference is lower than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>N</mi>
   </math>
   ).<br class=newline>
<p>
   Zero padding is also usefull to be in the range of application of the Fast-Fourier-Transform (FFT or Cooley-Tukey algorithm). This method, allows to reduce drastically the number of operations necessary to compute the DFT (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
      <msub>
         <mrow>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   ) but it requires that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   is a power of 2.<br class=newline>
<h3 class=sectionHead>Discrete systems</h3>
<p>
   The impulse response of a discrete LTI system
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold-italic>
         <mi>S</mi>
      </mstyle>
   </math>
   corresponds to the response of the system to a shock or impulsion
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   . This discrete signal is, by definition equal to unity for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and 0 elsewhere. The impulse response is denoted by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   . The causality principle implies
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   for negative values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   and the system is stable if there exists a number
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
   </math>
   so that for all
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mrow>
         <mo fence=true form=prefix>|</mo>
         <mrow>
            <mi>h</mi>
            <mo class=MathClass-open>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close>]</mo>
         </mrow>
         <mo fence=true form=postfix>|</mo>
      </mrow>
      <mo class=MathClass-rel><</mo>
      <mi>M</mi>
   </math>
   . As for continuous system, the response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   of the system to an excitation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   can be written with a convolution, which, in a discrete form, reads:
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <mo class=MathClass-bin>∗</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <mo class=MathClass-rel>=</mo>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
            </munder>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>m</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>[</mo>
            <mi>n</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>m</mi>
            <mo class=MathClass-close stretchy=false>]</mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>As for continuous time system, the methodology used in practice relies on a property of convolution; the DFT of a convolution between two functions is the product of the Fourier Transforms of the two functions.<br class=newline>
<p>
   The methodology to compute the discrete response of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold-italic>
         <mi>S</mi>
      </mstyle>
   </math>
   is presented in <a href=# data-img-alt="Figure 2. Response of a DT System <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=bold-italic><mi>S</mi></mstyle></math>." data-img-src="${config.ASSETS_URL}/03/16/theory/figure-2.svg">Figure 2</a>. We then need to compute the DFT of the impulse response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>[</mo>
      <mi>n</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   , called the Frequency Response Function (FRF)
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>H</mi>
      <mo class=MathClass-open>[</mo>
      <mi>k</mi>
      <mo class=MathClass-close>]</mo>
   </math>
   and multiply it by the DFT of the input signal. The DT response is finally obtained with an IDFT.
   <figure class=figure>
   <img alt="Figure 2. Response of a DT System <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=bold-italic><mi>S</mi></mstyle></math>." src="${config.ASSETS_URL}/03/16/theory/figure-2.svg">
   <figcaption class=caption>Figure 2. Response of a DT System <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=bold-italic><mi>S</mi></mstyle></math>.</figcaption>
   </figure>

<h3 class=sectionHead>Glossary</h3>
<dl>
  <dt>ADC
  <dd>Analog to Digital Converter 
  <dt>DFT
  <dd>Discrete Fourier Transform</dd>
  <dt>DT
  <dd>Discrete-Time</dd>
</dl>`;

export default theory;
